import tinymce, { Editor, Ui } from 'tinymce';

type InfoBox = {
  name: string;
  type: string;
  index: number | null;
  hasContent: boolean;
};

type Spec = Ui.Dialog.CheckboxSpec & {
  name: string;
};

const processResults = (editor: Editor, infoBoxes: InfoBox[]) => {
  const boxCards = infoBoxes
    .map(card => {
      const wasAdded = editor
        .getDoc()
        .querySelector(`.bc-infobox--${card.name}`);

      return card.hasContent && !wasAdded
        ? {
            type: 'checkbox',
            label: `InfoBox: ${card.type}${card.index ? `-${card.index}` : ''}`,
            name: card.name,
          }
        : null;
    })
    .filter((item): item is Spec => item !== null);

  if (boxCards.length > 0) return boxCards;

  editor.notificationManager.open({
    text: 'No additional images available.',
    type: 'info',
    timeout: 3000,
  });
};

const itemsList = (articleInfo?: string) => {
  const items = [];

  if (!articleInfo) return [];

  // Split the string by <h1> tags, but keep the <h1> tags in the resulting array
  const parts = articleInfo.split(/(?=<h1>)/);

  // Handle the first part before the first <h1> (if any)
  if (parts[0] && !parts[0].startsWith('<h1>')) {
    items.push(parts.shift()); // Add the content before the first <h1> as the first item
  }

  // Add the remaining parts, which each start with <h1>
  items.push(...parts);

  return items;
};

const openInfoBoxDialog = (editor: Editor) => {
  // Get infobox article and author content from Info Tab
  const form = document.querySelector<HTMLElement>('#form');
  const infoTab = form?.querySelector<HTMLElement>('#fieldset-infos');

  const infoBoxArticle = infoTab?.querySelector<HTMLElement>(
    `#formfield-form-widgets-background textarea`,
  );

  const inforBoxAuthor = infoTab?.querySelector<HTMLElement>(
    '#formfield-form-widgets-IAuthor-author_bio textarea',
  );

  if (!inforBoxAuthor || !infoBoxArticle) {
    editor.notificationManager.close();
    editor.notificationManager.open({
      text: 'Failed to load InfoBox Text, please try again and contact developers if the issue persists.',
      type: 'error',
      timeout: 5000,
    });

    console.error('Error getting InfoBox Editor field.');

    return;
  }

  const articleInfo = tinymce.get(infoBoxArticle.id)?.getContent();
  const authorInfo = tinymce.get(inforBoxAuthor.id)?.getContent();

  if (!authorInfo && !articleInfo)
    editor.notificationManager.open({
      text: 'No Information Box available. Please add on the Info tab.',
      type: 'info',
      timeout: 3000,
    });

  const parsedInfoboxes = itemsList(articleInfo);

  const infoBoxes = [
    ...parsedInfoboxes.map((articleInfo, idx) => {
      return {
        name: `articleInfo-${idx}`,
        type: 'articleInfo',
        index: idx + 1,
        hasContent: !!articleInfo,
      };
    }),
    {
      name: 'authorInfo',
      type: 'authorInfo',
      index: null,
      hasContent: !!authorInfo,
    },
  ];

  const boxCards = processResults(editor, infoBoxes);
  if (!boxCards) return;

  editor.windowManager.open({
    title: 'Information Cards',
    body: {
      type: 'panel',
      items: boxCards,
    },
    buttons: [
      { text: 'Insert', type: 'submit', primary: true },
      { text: 'Cancel', type: 'cancel' },
    ],
    onSubmit: api => {
      const data = api.getData();
      const content = Object.keys(data)
        .filter(id => data[id])
        .map(id => {
          const [type, index] = id.split('-');

          const content =
            (id === `articleInfo-${index}` && parsedInfoboxes[Number(index)]) ||
            (id === 'authorInfo' && authorInfo);
          if (!content) return;

          const infoboxHtml = `
              <div
                class="bc-infobox bc-infobox__editor bc-infobox--${type} bc-infobox--${id}"
                id="article-infobox"
                data-idx=${index || '-1'}
                contenteditable="false">
                    ${content}
              </div>
          `;

          return infoboxHtml;
        });

      const contentHtml = content.filter(item => item !== undefined)[0];
      if (!contentHtml) return;

      editor.insertContent(contentHtml, { format: 'raw' });
      api.close();
    },
  });
};

/**
 * Add a custom InfoBox button to the toolbar
 */
export const infobox = (editor: Editor) => {
  editor.ui.registry.addButton('infoboxButton', {
    icon: 'template-add',
    tooltip: 'Insert Info Box',
    onAction() {
      openInfoBoxDialog(editor);
    },
  });
};
